/**
 * Revenue Dashboard — Phase 1
 *
 * KPIs: revenue in period, outstanding invoices, completed jobs, avg job value
 * Chart: monthly revenue bar chart (last 6 months)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from 'recharts';
import {
  DollarSign, AlertCircle, Briefcase, TrendingUp,
  ArrowLeft, RefreshCw, Receipt,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast }  from 'sonner';
import axios from 'axios';
import ChartShell    from '../features/reports/components/ChartShell';
import KpiCard       from '../features/reports/components/KpiCard';
import PeriodSelector from '../features/reports/components/PeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtFull = (n) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Tooltip formatter for $ values
const DollarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill || p.stroke }}>
          {p.name}: {fmtFull(p.value)}
        </p>
      ))}
    </div>
  );
};

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const ReportsRevenue = () => {
  const [period,  setPeriod]  = useState('this_month');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res   = await axios.get(`${API}/api/reports/revenue?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load revenue report';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const periodLabel = {
    this_month: 'This Month',
    last_month: 'Last Month',
    last_3m:    'Last 3 Months',
    last_6m:    'Last 6 Months',
    this_year:  'This Year',
  }[period];

  // Trend helpers — null-safe; abs < 1 → neutral to avoid noise
  const getTrend = (pct) => {
    if (pct == null) return undefined;
    if (Math.abs(pct) < 1) return 'neutral';
    return pct > 0 ? 'up' : 'down';
  };
  const getDelta = (pct) => {
    if (pct == null) return undefined;
    return `${pct > 0 ? '+' : ''}${pct}%`;
  };
  const cmpLabel = data?.prevPeriodLabel; // e.g. "vs last month", or undefined

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">

      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <Link
            to="/reports"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Reports
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">Revenue Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(period)}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="revenue-kpi-grid">
        <KpiCard
          label={`Revenue — ${periodLabel}`}
          value={loading ? '…' : fmtFull(data?.revenueThisMonth ?? 0)}
          sub={cmpLabel || 'Paid invoices'}
          trend={getTrend(data?.revenueChange)}
          delta={getDelta(data?.revenueChange)}
          icon={DollarSign}
          iconColor="text-green-600"
          iconBg="bg-green-50"
          loading={loading}
          testId="kpi-revenue"
        />
        <KpiCard
          label="Outstanding Invoices"
          value={loading ? '…' : fmtFull(data?.outstandingAmount ?? 0)}
          sub={`${data?.outstandingCount ?? 0} unpaid`}
          icon={AlertCircle}
          iconColor="text-amber-500"
          iconBg="bg-amber-50"
          loading={loading}
          testId="kpi-outstanding"
        />
        <KpiCard
          label="Jobs Completed"
          value={loading ? '…' : String(data?.completedJobs ?? 0)}
          sub={cmpLabel || `in ${periodLabel.toLowerCase()}`}
          trend={getTrend(data?.jobsChange)}
          delta={getDelta(data?.jobsChange)}
          icon={Briefcase}
          iconColor="text-blue-500"
          iconBg="bg-blue-50"
          loading={loading}
          testId="kpi-completed-jobs"
        />
        <KpiCard
          label="Avg Job Value"
          value={loading ? '…' : fmtFull(data?.averageJobValue ?? 0)}
          sub={cmpLabel || 'Per completed job'}
          trend={getTrend(data?.avgJobValueChange)}
          delta={getDelta(data?.avgJobValueChange)}
          icon={TrendingUp}
          iconColor="text-orange-500"
          iconBg="bg-orange-50"
          loading={loading}
          testId="kpi-avg-job-value"
        />
        <KpiCard
          label="Tax Collected"
          value={loading ? '…' : fmtFull(data?.taxCollected ?? 0)}
          sub={cmpLabel || `in ${periodLabel.toLowerCase()}`}
          trend={getTrend(data?.taxChange)}
          delta={getDelta(data?.taxChange)}
          icon={Receipt}
          iconColor="text-violet-500"
          iconBg="bg-violet-50"
          loading={loading}
          testId="kpi-tax-collected"
        />
      </div>

      {/* Revenue Bar Chart */}
      <ChartShell
        title="Monthly Revenue"
        subtitle="Paid invoices grouped by month (last 6 months)"
        loading={loading}
        empty={!loading && (data?.revenueByMonth ?? []).every(m => m.revenue === 0)}
      >
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={data?.revenueByMonth ?? []}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis
              tickFormatter={fmt}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip content={<DollarTooltip />} cursor={{ fill: 'rgba(242,107,29,0.06)' }} />
            <Bar
              dataKey="revenue"
              name="Revenue"
              fill="#f26b1d"
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>

      {/* Invoice Count Trend Line Chart */}
      <ChartShell
        title="Invoice Volume"
        subtitle="Number of paid invoices per month (last 6 months)"
        loading={loading}
        empty={!loading && (data?.revenueByMonth ?? []).every(m => m.invoiceCount === 0)}
      >
        <ResponsiveContainer width="100%" height={220}>
          <LineChart
            data={data?.revenueByMonth ?? []}
            margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}
              cursor={{ stroke: 'rgba(242,107,29,0.2)', strokeWidth: 1 }}
            />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Line
              type="monotone"
              dataKey="invoiceCount"
              name="Invoices"
              stroke="#f26b1d"
              strokeWidth={2}
              dot={{ r: 3, fill: '#f26b1d' }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartShell>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ReportsRevenue;
